import { User } from '@markant-platform/shared/user/model';
import { BookingState } from './enums/booking-state';
import { PriceRequestState } from './enums/price-request-state';
import { PriceRequestStep } from './enums/price-request-step';
import { CdnImage, Video } from '@markant-platform/shared/content/model';
import { Address, OrganizationType } from 'data-model';

export enum CartridgeStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

/**
 * Type for the 'Cartridge' data
 */
export type Cartridge = {
  id: string;
  contentId?: string;
  key?: string;
  name: string;
  longName: string;
  description: string;
  longDescription: string;
  icon?: string;
  image?: string;
  cartridgeServicePackages?: CartridgeServicePackage[];
  variants: CartridgeVariant[];
  status: CartridgeStatus;
  bookedVariantId?: string;
  bookingState?: BookingState;
  priceRequestVariantId?: string;
  priceRequestState?: PriceRequestState;
  priceRequestType?: PriceRequestType;
  remoteEntry: string;
  widgets?: Widget[];
  apis?: CartridgeApi[];
  legalNoticeLinks: LegalNoticeLinkType[];
  cartridgeGroupKey: string;
  marketingInformation: CartridgeMarketingInformation[];
  cartridgeFeatures: CartridgeFeature[];
  serviceManagerId?: string;
  serviceManagerName?: string;
  serviceUpgradeRequestData?: ServiceUpgradeRequestData;
  serviceUpgradeState: ServiceUpgradeState | null;
  upgradeVariantId?: string;
};

export type CartridgeVariant = {
  variantId: string;
  contentId?: string;
  name: string;
  description: string;
  longDescription?: string;
  calculationParameters?: string[];
  icon?: string;
  image?: string;
  price: Price;
  prices?: Price[];
  remoteEntry?: string;
  widgets?: Widget[];
  apis?: CartridgeApi[];
  contractPeriod?: number;
  variantStatus: CartridgeStatus;
  bookingId?: string;
  priceRequestId?: string;
  allowUiBooking: boolean;
  allowEcsSelfBooking: boolean;
  setUpCost?: Price;
  requireIntegrationProject?: boolean;
  variantFeatures?: string[];
  usagePeriodInDays?: string;
  type: VariantType;
};

export type VariantType = 'STANDARD' | 'DEMO' | 'E_SERVICE';

export type CartridgeMarketingInformation = {
  informationContentType: 'text' | 'image' | 'fullWidthText' | 'video' | 'hero';
  heroTextAlignRight?: boolean;
  headline?: string;
  textInformation?: string;
  image?: CdnImage;
  video?: Video;
  informationContentTypeRight?: 'text' | 'image';
  headlineRight?: string;
  textInformationRight?: string;
  imageRight?: CdnImage;
};

export type CartridgeGroup = {
  id: string;
  cmsId?: string;
  key: string;
  name: string;
  description: string;
  image: string;
  icon: string;
};

export type CartridgeGroupBookingState = 'PARTIAL_BOOKED' | 'FULLY_BOOKED' | 'ONGOING_PROCESS' | 'NONE';

export type LegalNoticeLinkType = 'DEALER' | 'SUPPLIER' | 'DEALER_EZR' | 'SUPPLIER_EZR' | 'AUTO_SELECT';

export type CartridgeServicePackage = {
  id: string;
  name: string;
  cartridgeServices: CartridgeService[];
};

export type CartridgeService = {
  id: string;
  name: string;
};

export type FocusView = {
  routePath: string;
  exposedModule: string;
  ngModuleName?: string;
  elementName?: string;
};

export type Widget = {
  id: string;
  displayName: string;
  componentName?: string;
  elementName?: string;
  exposedModule: string;
  focusView?: FocusView;
  externalLink?: string;
  size: string;
  gridPositionX?: number;
  gridPositionY?: number;
};

export type BulkPriceModel = 'PRICE_SCALE_PER_UNIT' | 'STAGGERED_FIXED_PRICE' | 'PRICE_LEVEL_PER_UNIT';
export type PriceModel = 'SUBSCRIPTION' | 'PAY_PER_USE' | 'FIXED_PRICE' | BulkPriceModel;

export type BillingPeriod = 'NONE' | 'QUARTERLY' | 'MONTHLY' | 'YEARLY' | 'HALF_YEARLY';

export enum QuestionnaireAnswerType {
  FIXED = 'FIXED',
  FREE_TEXT = 'FREE_TEXT',
  CURRENCY_AMOUNT = 'CURRENCY_AMOUNT',
}

export type QuestionnaireAnswer = {
  [key: string]: string;
};

export type Question = {
  question: string;
  answerType: QuestionnaireAnswerType;
  hasFreeText?: boolean;
  fixedAnswers?: string[];
  infoText?: string;
  group: QuestionGroup;
};

export type QuestionGroup = 'COMMON' | 'PRICE_PARAMETER';

export type NegotiationRequestType = 'PRICE_REQUEST' | 'CONTACT_REQUEST';

export type Questionnaire = {
  questions: Question[];
};

export type Price = {
  currency: string; // ISO 3-Letter Currency Code
  centAmount: number;
  priceModel?: PriceModel;
  billingPeriod?: BillingPeriod;
  hasToBeRequested?: boolean;
  targetAudience: TargetAudience;
  questionnaire?: Questionnaire;
  bulkPrices?: BulkPrice[];
};

export type SetUpCost = {
  currency: string; // ISO 3-Letter Currency Code
  centAmount: number;
};

export type TargetAudience = {
  organizationType: OrganizationType;
  ezr: boolean;
};

export type BulkPrice = {
  cartridgeServicePackageId: string;
  tiers: Tier[];
};

export type Tier = {
  minimalNumber: number;
  decimilliesAmount: number;
};

export type BookingDraft = {
  cartridgeVariantId: string;
  user: User;
  references?: References;
  serviceGtcAccepted: boolean;
};

export type References = {
  customerCartridgeReferenceId?: string;
  costCenter?: string;
  otherReferences?: string;
};

export type ServiceUpgradeType = 'UPGRADE_REQUEST' | 'PRICE_REQUEST';

export type ServiceUpgradeRequestData = {
  cartridgeId: string;
  cartridgeVariantId: string;
  type: ServiceUpgradeType;
  createdAt: string;
  createdBy: string;
  upgradeCompletedAt: string;
  version: number;
};
export type ServiceUpgradeState = 'UPGRADE_REQUEST_OPEN' | 'UPGRADE_REQUEST_APPROVED' | 'UPGRADE_SUGGESTED';

export type BookingRequestType = 'ORDER_REQUEST' | 'UPGRADE_REQUEST' | 'DEMO_REQUEST';

export const UpgradeSuggestedStoragePrefix = 'UPGRADE_SUGGESTED';

export type BookingData = {
  id: string;
  cartridgeId: string;
  cartridgeVariantId: string;
  cartridgeName: string;
  cartridgeVariantName: string;
  cartridgeVariantDescription?: string;
  cartridgeServicePackageData?: CartridgeServicePackage[];
  price: Price;
  setUpCost?: SetUpCost;
  bookingDate: string;
  lastModifiedAt: string;
  user: string;
  userFirstname: string;
  userLastname: string;
  responsibleUserId?: string;
  responsibleUserFirstname?: string;
  responsibleUserLastname?: string;
  responsibleUserEmail?: string;
  organizationId: string;
  organizationName: string;
  bookingState: BookingState;
  priceRequestId?: string;
  version: number;
  cancellationDate?: string;
  workflowFinishedAt?: string;
  usagePeriodEndDate: string | null;
  startedBookingByUserName?: string;
  bookingStartedAt?: string;
  hasLegalDocument: boolean;
  serviceUpgradeRequestData?: ServiceUpgradeRequestData;
  serviceUpgradeState: ServiceUpgradeState | null;
  customerCartridgeReferenceId?: string;
  otherReferences?: string;
  costCenter?: string;
  platformBilling?: boolean;
  campaignId?: string;
  leadContext?: string;
  blocked?: boolean;
  requestType?: BookingRequestType;
};

export type BookingDataDetails = BookingData & {
  organizationAddress: Address;
  initiator: User;
  reviewedByUserName?: string;
  platformBilling?: boolean;
  rejection?: Rejection;
};

export type BookingDataWithUpgradeInfo = BookingDataDetails & {
  contractStartDate: string | null;
  upgradableCartridgeVariantIds: string[];
  usagePeriodEndDate?: string;
};

export type Rejection = {
  rejectedBy: string;
  rejectedAt: string;
  reason: string;
};

export type NegotiationRequestParams = {
  cartridgeVariantId: string;
  type: NegotiationRequestType;
  priceRequestId?: string;
};

export type BookingPriceRequest = {
  id: string;
  cartridgeId: string;
  variantId: string;
  offerNumber: string;
  offerPrice: Price;
};

export type CartridgeApi = {
  apiStatus: string;
  id: string;
  pathPrefix: string;
  serviceUrl: string;
  spec: string;
};

export type BookingCancellationDate = {
  cancellationDate: string;
};

export type CartridgeProjection = {
  variantId: string;
  cartridgeId: string;
  name: string;
  description: string;
  variantName: string;
  variantDescription: string;
  remoteEntry: string;
  apis: CartridgeApi[];
  widgets: Widget[];
};

export type BookingRequestAudit = {
  status: BookingState;
  reviewedBy: string;
  orderConfirmationDate: string;
  startUsageDate: Date | string; // date and handle empty string
};

export type ContactPerson = {
  salutation: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
};

export type PriceRequestDraft = {
  cartridgeVariantId: string;
  contactPerson?: ContactPerson;
  questions?: QuestionnaireAnswer;
};

export type PriceRequestBookingData = {
  cartridgeId: string;
  cartridgeVariantId: string;
  cartridgeName: string;
  cartridgeVariantName: string;
};

export type PriceRequestType = 'ORDER_REQUEST' | 'UPGRADE_REQUEST';

export type PriceRequestOAData = {
  id: string;
  cartridge?: PriceRequestBookingData;
  requestDate: string;
  state: PriceRequestState;
  requesterName: string;
  offerCreatedAt: string;
  offerValidUntil: string;
  offerNumber: string;
  offerPrice?: Price;
  type?: PriceRequestType;
  version: number;
};

export type OrderRequestsDto = {
  pendingRequests: number;
  acceptedRequest: number;
  acceptedUpgradeRequests: number;
  pendingUpgradeRequests: number;
};

export type ContactRequestsDto = {
  openNegotiationRequests: number;
  inProgressNegotiationRequests: number;
};

export type PriceRequestsDto = {
  newRequests: number;
  inProgressRequests: number;
  upgradeOpenRequests: number;
  upgradeInProgressRequests: number;
};

export type ContactRequestDetailsDto = {
  cartridgeId: string;
  cartridgeName: string;
  cartridgeVariantId: string;
  cartridgeVariantName: string;
  comments: ContactRequestDetailsComments[];
  createdAt: string;
  email: string;
  id: string;
  lastModifiedAt: string;
  name: string;
  organization: string;
  status: RequestState;
  telephoneNumber: string;
  version: number;
  priceRequestId?: string;
};

export type ContactRequestDetailsComments = {
  comment: string;
  createdAt: string;
  name: string;
  userId: string;
};

export interface ContactRequestsData {
  hasNext: boolean;
  pageIndex: number;
  pageSize: number;
  result: ContactRequestsDataDetails[];
  totalCount: number;
}

export interface ContactRequestsDataDetails {
  id: string;
  cartridgeVariantId: string;
  cartridgeVariantName: string;
  organization: string;
  name: string;
  status: RequestState;
  lastModifiedAt: string;
  createdAt: string;
  version: number;
  type: string;
  cartridgeId: string;
  cartridgeName: string;
}

export type RequestState = 'OPEN' | 'IN_PROGRESS' | 'DONE';

export interface ContactRequestUniqueData {
  organizations: string[];
  variants: [
    {
      [key: string]: string;
    },
  ];
}

export interface UpdateRequestData {
  comments: string[];
  status: RequestState;
  version: number;
}

export type PriceRequestInfo = {
  step: PriceRequestStep;
  cartridge?: Cartridge;
  cartridgeVariant?: CartridgeVariant;
  contactPerson?: ContactPerson;
  questions?: QuestionnaireAnswer;
};

export type CartridgeFeature = { contentId: string; description: string; sortOrder: number };

export type UpgradeDraft = {
  upgradeToVariantId: string;
  platformBilling?: boolean;
  centAmount?: number;
  bookingReference?: string;
  version: number;
};
